import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * The OrderedList component is an ordinary `<ol>` element,
 * used for normal numeration lists in editorial content.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-ordered-list.html
 *
 * @param {boolean} [nestedCounter] - Apply numeration on nested `<ol>`.
 * @param {Item[]} [children] - The nested {@link Item} elements.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {Object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The {@link OrderedList} component.
 * @constructor
 */
export function OrderedList({ nestedCounter, testId, children, ...otherProps }) {
  const olClassNameList = classNames({
    'c-ol': !nestedCounter,
    'c-list-nested-counter': nestedCounter,
  }).trim()

  return (
    <ol {...otherProps} data-testid={testId} className={olClassNameList}>
      {children /* Use 'OrderedList.Item' component. */}
    </ol>
  )
}

OrderedList.propTypes = {
  nestedCounter: PropTypes.bool, // Bronson template: 'ordered-list-nested-counter'.
  testId: PropTypes.string, // Added for data-testid attribute.
  children: PropTypes.node, // Bronson template: 'item'. Use 'OrderedList.Item' component.
}

/**
 * The Item component (nested) is internally used to wrap
 * the item content and display it inside the {@link OrderedList}.
 *
 * @param {React.ReactNode} [children] - The {@link Item} content.
 * @param {Object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The {@link Item} component.
 */
function Item({ children, ...otherProps }) {
  return <li {...otherProps}>{children}</li>
}

Item.propTypes = {
  children: PropTypes.node, // Bronson template: 'content'.
}

Item.displayName = 'OrderedList.Item'
OrderedList.Item = Item
